// Typed client for the LabConnect identity API (Phase C surface): sessions,
// MFA challenge flow, account email flows, memberships, and invitations.
// The SPA keeps the session token in memory and sends Bearer; the API also
// sets an HttpOnly cookie for same-origin browsing (Phase H).
import { errorFrom } from './index';
async function call(opts, method, path, body) {
    const f = opts.fetchImpl ?? fetch;
    const headers = {};
    if (opts.sessionToken) {
        headers.Authorization = `Bearer ${opts.sessionToken}`;
    }
    const init = { method, headers };
    if (body !== undefined) {
        headers['Content-Type'] = 'application/json';
        init.body = JSON.stringify(body);
    }
    const res = await f(new URL(path, opts.baseUrl), init);
    if (!res.ok) {
        throw await errorFrom(res, path);
    }
    return res;
}
async function json(opts, method, path, body) {
    return (await (await call(opts, method, path, body)).json());
}
/** Password login; discriminates the MFA challenge from a plain session. */
export async function login(opts, email, password) {
    const raw = await json(opts, 'POST', '/api/auth/login', { email, password });
    if (raw.mfaRequired === true && typeof raw.mfaToken === 'string') {
        return { kind: 'mfa', mfaToken: raw.mfaToken };
    }
    return { kind: 'session', session: raw };
}
/** Complete an MFA challenge with an authenticator code. */
export function verifyMfa(opts, mfaToken, code) {
    return json(opts, 'POST', '/api/auth/mfa/verify', { mfaToken, code });
}
/** Complete an MFA challenge with a single-use recovery code. */
export function recoverMfa(opts, mfaToken, recoveryCode) {
    return json(opts, 'POST', '/api/auth/mfa/recover', { mfaToken, recoveryCode });
}
export function me(opts) {
    return json(opts, 'GET', '/api/auth/me');
}
export async function logout(opts) {
    await call(opts, 'POST', '/api/auth/logout');
}
/** Step-up: re-verify credentials to refresh the session's fresh-auth window for
 * high-risk actions. Provide `code` when the account has MFA enabled. */
export async function stepUp(opts, password, code) {
    await call(opts, 'POST', '/api/auth/step-up', { password, code: code ?? null });
}
/** Always resolves regardless of account existence (server is oracle-free). */
export async function forgotPassword(opts, email) {
    await call(opts, 'POST', '/api/auth/forgot-password', { email });
}
export async function resetPassword(opts, token, newPassword) {
    await call(opts, 'POST', '/api/auth/reset-password', { token, newPassword });
}
export async function sendVerification(opts) {
    await call(opts, 'POST', '/api/auth/send-verification');
}
export async function verifyEmail(opts, token) {
    await call(opts, 'POST', '/api/auth/verify-email', { token });
}
/** The signed-in user's tenants (tenant switcher). */
export function myMemberships(opts) {
    return json(opts, 'GET', '/api/me/memberships');
}
/** Accept an emailed invitation as the signed-in user. */
export function acceptInvitation(opts, token) {
    return json(opts, 'POST', '/api/invitations/accept', { token });
}
/** Begin MFA enrollment: returns the pending secret to load into an app. */
export function setupMfa(opts) {
    return json(opts, 'POST', '/api/auth/mfa/setup');
}
/** Arm MFA by proving a current code; returns recovery codes shown ONCE. */
export async function enableMfa(opts, code) {
    const raw = await json(opts, 'POST', '/api/auth/mfa/enable', { code });
    return raw.recoveryCodes;
}
/** Disable MFA (requires a current code); burns all recovery codes. */
export async function disableMfa(opts, code) {
    await call(opts, 'POST', '/api/auth/mfa/disable', { code });
}
/** The signed-in user's active sessions, current one marked. */
export function listSessions(opts) {
    return json(opts, 'GET', '/api/auth/sessions');
}
/** Revoke every session (including this one). Returns the count revoked. */
export async function revokeAllSessions(opts) {
    const raw = await json(opts, 'POST', '/api/auth/sessions/revoke-all');
    return raw.revoked;
}
/** The public plan catalog (no prices). Anonymous — no session needed. */
export function billingPlans(opts) {
    return json(opts, 'GET', '/api/billing/plans');
}
/** The active tenant's entitlements and subscription. Any member may read. */
export function tenantBilling(opts, tenantId) {
    return json(opts, 'GET', `/api/tenants/${tenantId}/billing`);
}
/** Begin hosted checkout for a plan; returns the provider redirect URL. */
export async function startCheckout(opts, tenantId, planId) {
    const raw = await json(opts, 'POST', `/api/tenants/${tenantId}/billing/checkout`, { planId });
    return raw.url;
}
/** Open the provider's billing portal; returns the redirect URL. */
export async function openBillingPortal(opts, tenantId) {
    const raw = await json(opts, 'POST', `/api/tenants/${tenantId}/billing/portal`);
    return raw.url;
}
//# sourceMappingURL=auth.js.map